import { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Select, InputNumber, message, Input, Checkbox, Row, Col, Divider, Typography, Alert, Popconfirm, Tooltip, Statistic } from 'antd';
import { ApiOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, CheckOutlined, DeleteOutlined, ThunderboltOutlined, StarOutlined, DollarOutlined, CalculatorOutlined } from '@ant-design/icons';
import { adminAPI, providerAPI, internalGatewayAPI } from '../services/api';

const { Text, Title } = Typography;
const { Search } = Input;

// OpenRouter 模型分类
const MODEL_CATEGORIES = {
  'openai': { label: 'OpenAI', color: '#10a37f' },
  'anthropic': { label: 'Anthropic', color: '#d97706' },
  'deepseek': { label: 'DeepSeek', color: '#6366f1' },
  'google': { label: 'Google', color: '#4285f4' },
  'meta-llama': { label: 'Meta Llama', color: '#ff6b6b' },
  'mistralai': { label: 'Mistral', color: '#f59e0b' },
  'qwen': { label: 'Qwen', color: '#06b6d4' },
  'x-ai': { label: 'xAI Grok', color: '#8b5cf6' },
  'cohere': { label: 'Cohere', color: '#059669' },
  'other': { label: '其他', color: '#6b7280' },
};

// 获取模型分类
const getModelCategory = (modelName) => {
  const parts = modelName.split('/');
  if (parts.length >= 1) {
    const provider = parts[0]; // 如 deepseek/deepseek-v4-pro，取 parts[0] = "deepseek"
    return MODEL_CATEGORIES[provider] || MODEL_CATEGORIES['other'];
  }
  return MODEL_CATEGORIES['other'];
};

// 获取模型简称
const getShortModelName = (modelName) => {
  const parts = modelName.split('/');
  return parts[parts.length - 1] || modelName;
};

// 格式化价格显示（确保显示完整小数）
const formatPrice = (price, decimals = 8) => {
  if (price === 0) return '0';
  return price.toFixed(decimals).replace(/\.?0+$/, ''); // 去除尾部多余的0
};

// 计算加价后的价格（平台与 OpenRouter 同为 $/1M tokens，不再换算）
const calculateMarkupPrice = (price, markupPercent) => {
  return price * (1 + markupPercent / 100);
};

const OpenRouterModels = () => {
  const [availableModels, setAvailableModels] = useState([]);  // OpenRouter 可用模型
  const [addedModels, setAddedModels] = useState([]);           // 已添加的模型
  const [loading, setLoading] = useState(false);
  const [fetchingAvailable, setFetchingAvailable] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [markupPercent, setMarkupPercent] = useState(5); // 加价比例（百分比）
  const [previewPrices, setPreviewPrices] = useState({ input: 0, output: 0 }); // 价格预览
  const [openrouterProviderId, setOpenrouterProviderId] = useState(null); // OpenRouter 供应商 ID

  // 加载 OpenRouter 供应商 ID
  const loadOpenRouterProviderId = async () => {
    try {
      const res = await adminAPI.getProviders();
      console.log('Providers response:', res);
      // 处理不同的响应结构
      let providers = [];
      if (Array.isArray(res)) {
        providers = res;
      } else if (res?.data && Array.isArray(res.data)) {
        providers = res.data;
      } else if (res?.data?.list && Array.isArray(res.data.list)) {
        providers = res.data.list;
      } else if (res?.providers && Array.isArray(res.providers)) {
        providers = res.providers;
      }
      
      const openrouterProvider = providers.find(p => p.name === 'openrouter');
      if (openrouterProvider) {
        setOpenrouterProviderId(openrouterProvider.id);
      }
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  // 加载已添加的模型
  const loadAddedModels = async () => {
    try {
      const res = await adminAPI.getModelConfigs();
      if (res.code === 0) {
        // 通过 provider_id 或 provider.name 识别 OpenRouter 模型
        const openrouterModels = (res.data?.list || []).filter(m => 
          m.provider_id === openrouterProviderId || m.provider?.name === 'openrouter'
        );
        setAddedModels(openrouterModels);
      }
    } catch (error) {
      console.error('Failed to load added models:', error);
    }
  };

  // 获取 OpenRouter 可用模型列表
  const fetchAvailableModels = async () => {
    try {
      setFetchingAvailable(true);
      // 确保已加载已添加的模型
      if (addedModels.length === 0) {
        await loadAddedModels();
      }
      // 直接获取 OpenRouter 模型列表（不自动同步，只获取用于选择）
      const modelsData = await internalGatewayAPI.get('/sync/openrouter/models');
      if (modelsData.models) {
        // 转换为可选择格式 { name, input_price, output_price, context_window, vision_support }
        const modelDetails = modelsData.models.map(m => ({
          name: m.name,
          input_price: m.input_price,
          output_price: m.output_price,
          context_window: m.context_window,
          vision_support: m.vision_support,
        }));
        
        // 过滤掉已添加的模型（根据 model_name 匹配）
        const addedModelNames = addedModels.map(m => m.model_name);
        const availableOnly = modelDetails.filter(m => !addedModelNames.includes(m.name));
        
        setAvailableModels(availableOnly);
        if (addedModelNames.length > 0) {
          message.info(`OpenRouter 共 ${modelDetails.length} 个模型，已添加 ${addedModelNames.length} 个，可添加 ${availableOnly.length} 个`);
        } else {
          message.info(`OpenRouter 共 ${modelDetails.length} 个模型待添加`);
        }
        setAddModalVisible(true);
      }
    } catch (error) {
      console.error('Failed to fetch available models:', error);
      message.error('获取 OpenRouter 模型列表失败');
    } finally {
      setFetchingAvailable(false);
    }
  };

  // 过滤后的模型列表
  const filteredModels = availableModels.filter(model => {
    const matchSearch = searchText === '' || 
      model.name.toLowerCase().includes(searchText.toLowerCase()) ||
      getShortModelName(model.name).toLowerCase().includes(searchText.toLowerCase());
    // 通过检查模型 ID 的前缀来匹配分类（如 deepseek/deepseek-v4-pro 匹配 deepseek 分类）
    const matchCategory = categoryFilter === 'all' || model.name.startsWith(`${categoryFilter}/`);
    return matchSearch && matchCategory;
  });

  // 添加选中的模型
  const handleAddModels = async () => {
    if (selectedModels.length === 0) {
      message.warning('请选择要添加的模型');
      return;
    }

    try {
      setLoading(true);
      const values = await form.validateFields();
      // 确保 markup 是数字类型（百分比，如 5 表示 5%）
      const markup = parseFloat(values.markup) || 0;
      
      // 构建添加请求 - 使用模型原有的价格
      const modelsToAdd = selectedModels.map(model => {
        const shortName = getShortModelName(model.name);
        return {
          model_name: model.name,
          display_name: shortName,
          // 使用 OpenRouter 原始价格，后端批量接口会根据 markup 百分比加价
          input_price: model.input_price || 0,
          output_price: model.output_price || 0,
          context_window: model.context_window || 32000,
          vision_support: model.vision_support || model.name.includes('vision') || model.name.includes('image'),
          plugin_support: true,
        };
      });

      // 调用后端批量添加接口，传递 markup 百分比和模型分类
      await providerAPI.batchAddModels({
        provider_id: openrouterProviderId, // 动态获取的 OpenRouter 供应商 ID
        models: modelsToAdd,
        markup: markup,
        model_type: values.model_type || 'text', // 模型分类
      });

      message.success(`成功添加 ${selectedModels.length} 个模型`);
      setAddModalVisible(false);
      setSelectedModels([]);
      setSelectedRowKeys([]);
      loadAddedModels();
    } catch (error) {
      console.error('Failed to add models:', error);
      message.error('添加模型失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除模型
  const handleDeleteModel = async (modelId, modelName) => {
    try {
      const response = await adminAPI.deleteModelConfig(modelId);
      if (response.code === 0) {
        message.success('删除成功');
        loadAddedModels();
        await adminAPI.refreshGatewayModels().catch(() => {});
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      console.error('Delete model error:', error);
      message.error('删除失败: ' + (error?.message || error?.response?.data?.message || '未知错误'));
    }
  };

  // 表格列定义 - 已添加模型
  const addedColumns = [
    {
      title: '模型标识',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 200,
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 180,
    },
    {
      title: '输入价格',
      dataIndex: 'input_price',
      key: 'input_price',
      width: 120,
      render: (val) => `¥${formatPrice(val) || 0}/M`,
    },
    {
      title: '输出价格',
      dataIndex: 'output_price',
      key: 'output_price',
      width: 120,
      render: (val) => `¥${formatPrice(val) || 0}/M`,
    },
    {
      title: '上下文',
      dataIndex: 'context_window',
      key: 'context_window',
      width: 100,
      render: (val) => val ? `${(val / 1000).toFixed(0)}K` : '-',
    },
    {
      title: '视觉',
      dataIndex: 'vision_support',
      key: 'vision_support',
      width: 80,
      render: (val) => val ? <Tag color="green">支持</Tag> : <Tag>不支持</Tag>,
    },
    {
      title: '函数调用',
      dataIndex: 'plugin_support',
      key: 'plugin_support',
      width: 100,
      render: (val) => val ? <Tag color="blue">支持</Tag> : <Tag>不支持</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={`确定删除模型 "${record.display_name || record.model_name}" 吗？`}
          onConfirm={() => handleDeleteModel(record.id, record.model_name)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger size="small">删除</Button>
        </Popconfirm>
      ),
    },
  ];

  // 加载数据
  useEffect(() => {
    loadOpenRouterProviderId().then(() => {
      loadAddedModels();
    });
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <ApiOutlined />
            <span>OpenRouter 模型管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={fetchAvailableModels}
              loading={fetchingAvailable}
            >
              添加模型
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={loadAddedModels}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic 
              title="已添加模型" 
              value={addedModels.length} 
              prefix={<ApiOutlined />} 
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="支持视觉" 
              value={addedModels.filter(m => m.vision_support).length} 
              prefix={<StarOutlined />} 
            />
          </Col>
          <Col span={12}>
            <Alert
              message="使用说明"
              description="点击「添加模型」从 OpenRouter 获取可用模型列表，勾选需要的模型后批量添加。已添加的模型可在下方列表中查看和删除。"
              type="info"
              showIcon
            />
          </Col>
        </Row>

        <Table
          columns={addedColumns}
          dataSource={addedModels}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      {/* 添加模型弹窗 */}
      <Modal
        title={
          <Space>
            <ApiOutlined />
            <span>添加 OpenRouter 模型</span>
          </Space>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          setSelectedModels([]);
          setSelectedRowKeys([]);
        }}
        width={1200}
        footer={[
          <Button key="cancel" onClick={() => setAddModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="add" 
            type="primary" 
            loading={loading}
            onClick={handleAddModels}
            disabled={selectedModels.length === 0}
          >
            添加 {selectedModels.length > 0 && `(${selectedModels.length})`}
          </Button>,
        ]}
      >
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="markup" label="加价比例" initialValue={5}>
            <Space.Compact>
              <InputNumber 
                min={-50} 
                max={500} 
                step={1} 
                style={{ width: 100 }} 
                placeholder="如 5 表示加价5%"
                onChange={(val) => setMarkupPercent(val ?? 0)}
              />
              <span style={{ lineHeight: '32px', padding: '0 11px', background: '#fafafa', border: '1px solid #d9d9d9', borderLeft: 0, borderRadius: '0 6px 6px 0' }}>%</span>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="model_type" label="模型分类" initialValue="text">
            <Select style={{ width: 120 }}>
              <Select.Option value="text">对话</Select.Option>
              <Select.Option value="image">图像</Select.Option>
              <Select.Option value="embeddings">嵌入</Select.Option>
              <Select.Option value="audio">语音</Select.Option>
              <Select.Option value="video">视频</Select.Option>
              <Select.Option value="speech">语音合成</Select.Option>
              <Select.Option value="transcription">语音识别</Select.Option>
              <Select.Option value="rerank">重排序</Select.Option>
            </Select>
          </Form.Item>
        </Form>
        
        {/* 价格预览 */}
        <Alert
          message={
            <Space>
              <CalculatorOutlined />
              <span>价格预览</span>
              <Tag color="blue">加价 {markupPercent}%</Tag>
            </Space>
          }
          description={
            <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
              <div>
                <Text type="secondary">输入价格：</Text>
                <Text strong style={{ color: '#52c41a' }}>
                  ¥{calculateMarkupPrice(previewPrices.input, markupPercent).toFixed(4)}/1M
                </Text>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  (原始: ¥{previewPrices.input.toFixed(4)})
                </Text>
              </div>
              <div>
                <Text type="secondary">输出价格：</Text>
                <Text strong style={{ color: '#fa8c16' }}>
                  ¥{calculateMarkupPrice(previewPrices.output, markupPercent).toFixed(4)}/1M
                </Text>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  (原始: ¥{previewPrices.output.toFixed(4)})
                </Text>
              </div>
            </div>
          }
          type="info"
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Search
              placeholder="搜索模型..."
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={12}>
            <Select
              style={{ width: '100%' }}
              placeholder="按品牌筛选"
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
            >
              <Select.Option value="all">全部品牌</Select.Option>
              {Object.entries(MODEL_CATEGORIES).map(([key, val]) => (
                <Select.Option key={key} value={key}>
                  <Tag color={val.color}>{val.label}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div style={{ marginBottom: 8 }}>
          <Button 
            size="small" 
            onClick={() => {
              // 选中所有过滤后的模型
              const newSelected = [...selectedModels];
              filteredModels.forEach(m => {
                if (!newSelected.some(s => s.name === m.name)) {
                  newSelected.push(m);
                }
              });
              setSelectedModels(newSelected);
              setSelectedRowKeys(newSelected.map(m => m.name));
            }}
          >
            全选当前页
          </Button>
          <Button 
            size="small" 
            style={{ marginLeft: 8 }}
            onClick={() => {
              setSelectedModels([]);
              setSelectedRowKeys([]);
            }}
          >
            取消全选
          </Button>
          <Text type="secondary" style={{ marginLeft: 16 }}>
            已选择 {selectedModels.length} 个模型
          </Text>
        </div>

        <div style={{ maxHeight: 500, overflowY: 'auto', border: '1px solid #f0f0f0', padding: 8 }}>
          {filteredModels.map(model => {
            const isSelected = selectedModels.some(m => m.name === model.name);
            const category = getModelCategory(model.name);
            const shortName = getShortModelName(model.name);
            const finalInputPrice = calculateMarkupPrice(model.input_price || 0, markupPercent);
            const finalOutputPrice = calculateMarkupPrice(model.output_price || 0, markupPercent);
            
            return (
              <div
                key={model.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  margin: '4px 0',
                  background: isSelected ? '#e6f7ff' : 'transparent',
                  borderRadius: 4,
                  border: isSelected ? '1px solid #91d5ff' : '1px solid transparent',
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedModels([...selectedModels, model]);
                    } else {
                      setSelectedModels(selectedModels.filter(m => m.name !== model.name));
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Tag color={category.color}>{category.label}</Tag>
                  <Text strong={isSelected}>{shortName}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{model.name}</Text>
                  {model.vision_support && (
                    <Tag color="green" style={{ fontSize: 10 }}>视觉</Tag>
                  )}
                  {model.name.includes(':free') && (
                    <Tag color="gold" style={{ fontSize: 10 }}>免费</Tag>
                  )}
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                    <Text code>入 ¥{formatPrice(model.input_price)}</Text>{' '}
                    <Text code>出 ¥{formatPrice(model.output_price)}</Text>
                    {markupPercent !== 0 && (
                      <Text style={{ marginLeft: 8 }}>
                        → <Text strong style={{ color: '#52c41a' }}>入 ¥{formatPrice(finalInputPrice)}</Text>{' '}
                        <Text strong style={{ color: '#fa8c16' }}>出 ¥{formatPrice(finalOutputPrice)}</Text>
                      </Text>
                    )}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>

        {filteredModels.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            <Text>没有找到匹配的模型</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OpenRouterModels;
